import { useMaintenanceStatus } from '@/api/hooks/status';
import { LoadingScreen } from '@/components/LoadingScreen';
import { usePrincipal } from '@/hooks/usePrincipal';
import type * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

/**
 * Universal pre-render gate mounted as the routes config's top-level
 * parent. Runs the maintenance-mode check BEFORE auth so anonymous
 * visitors see the maintenance page too (not a broken `/auth/login`). Children
 * render with full router context — `<Link>`, `useNavigate`, etc. work
 * inside `MaintenancePage`, `PublicLanding`, etc.
 *
 * Decision tree (in order):
 *
 *  1. Path is an unauth recovery surface (`/maintenance` or `/auth/login`) →
 *     render through unconditionally. `/maintenance` is in the set to
 *     break the redirect-to-self loop; `/auth/login` is in the set so the
 *     "Admin sign-in" link on the maintenance page is actually reachable
 *     — otherwise an anon visitor (or any caller without
 *     `maintenance:bypass`) clicks the link, navigates to `/auth/login`, and
 *     gets bounced straight back by step 3 before the Login page even
 *     mounts. The click appears inert.
 *  2. Principal loading, OR the status query's FIRST load → `<LoadingScreen/>`.
 *     We block on the principal AND the status query so we never paint a
 *     "redirect to maintenance" decision against stale auth state — an admin
 *     holding `maintenance:bypass` would briefly see the maintenance page
 *     otherwise. Crucially the status arm gates on `isInitialLoad` (first
 *     load only), NOT `isLoading`: once the status query settles for the first
 *     time (`isFetched` latches `true`) it never repaints `<LoadingScreen/>` on
 *     a later refetch — including the errored-first-load case where `data` is
 *     never cached and a failing 30s poll can flip `isLoading` back to `true`
 *     (the latched `isFetched` keeps `isInitialLoad` `false`). That closes the
 *     poll-flicker path where a signed-out visitor on a stable landing page
 *     saw the splash blink every 30s.
 *  3. Maintenance on AND caller lacks `maintenance:bypass` →
 *     `<Navigate to="/maintenance" replace/>`. Super-admins keep their
 *     escape hatch (so they can flip the kill-switch back off); everyone
 *     else, anon or authed, bounces.
 *  4. Otherwise → `<Outlet/>`. Auth gating is the authed sub-tree's job
 *     (`AuthedLayout`), not this gate's.
 *
 * Status fail-open posture lives in `useMaintenanceStatus()` —
 * loading-or-errored resolves to `maintenance: false` so a transient
 * `/api/status` 5xx doesn't lock the whole app out.
 *
 * Principal `'error'` branch: surfaces as fail-closed (no bypass perm) —
 * the gate falls through to step 3/4. A non-401/403 /api/auth/me failure
 * is rare enough that route-by-route error UI doesn't earn its keep at
 * this layer; the LoadingScreen burn during the brief retry window is
 * acceptable.
 */
const BYPASS_PERMISSIONS = ['maintenance:bypass'] as const;
const UNAUTH_SURFACES = new Set(['/maintenance', '/auth/login', '/auth/callback', '/auth/logout']);

export function RootGate(): React.ReactElement {
  const location = useLocation();
  const { isInitialLoad, maintenance } = useMaintenanceStatus();
  const principal = usePrincipal();

  // 1. Short-circuit unauth recovery surfaces.
  if (UNAUTH_SURFACES.has(location.pathname)) {
    return <Outlet />;
  }

  // 2. Block on both queries.
  if (isInitialLoad || principal.status === 'loading') {
    return <LoadingScreen />;
  }

  // 3. Maintenance gate. `hasAnyPermission` is only available on the
  // authenticated branch — anon/error principals cannot hold
  // `maintenance:bypass` by definition, so the bypass check is `false`
  // for them and they fall through to the redirect.
  const canBypass =
    principal.status === 'authenticated' && principal.hasAnyPermission(BYPASS_PERMISSIONS);
  if (maintenance && !canBypass) {
    return <Navigate to="/maintenance" replace />;
  }

  // 4. Pass-through.
  return <Outlet />;
}
